import React, { useMemo, useState } from 'react';
import { CollectionPreferences, Table } from '@cloudscape-design/components';

const storageKey = id => `operations-report-table-${id}-v1`;
const loadColumns = id => {
  try {
    return JSON.parse(window.localStorage.getItem(storageKey(id)));
  } catch {
    return null;
  }
};

const resolveColumns = (saved, columns) => {
  const remaining = new Set(columns.map(column => column.id));
  const display = [];
  if (Array.isArray(saved)) saved.forEach(entry => {
    if (!remaining.has(entry?.id)) return;
    display.push({ id: entry.id, visible: entry.id === columns[0].id || entry.visible !== false });
    remaining.delete(entry.id);
  });
  remaining.forEach(id => display.push({ id, visible: true }));
  return display;
};

export default function OperationsReportTable({ id, title, items, columnDefinitions, loading, empty, summary }) {
  const [savedColumns, setSavedColumns] = useState(() => loadColumns(id));
  const [sort, setSort] = useState(null);
  const columns = useMemo(() => columnDefinitions.map(column => ({ ...column, sortingField: column.id })), [columnDefinitions]);
  const columnDisplay = useMemo(() => resolveColumns(savedColumns, columns), [savedColumns, columns]);
  const sortingColumn = columns.find(column => column.id === sort?.id);
  const sortedItems = useMemo(() => {
    if (!sortingColumn) return items;
    return [...items].sort((left, right) => {
      // The overall total is a summary, not another region to rank.
      if (left.isTotal || right.isTotal) return Number(Boolean(left.isTotal)) - Number(Boolean(right.isTotal));
      const a = left[sortingColumn.sortingField];
      const b = right[sortingColumn.sortingField];
      const comparison = typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a ?? '').localeCompare(String(b ?? ''), 'en', { numeric: true, sensitivity: 'base' });
      return sort.isDescending ? -comparison : comparison;
    });
  }, [items, sort, sortingColumn]);

  const confirmColumns = ({ detail }) => {
    const next = resolveColumns(detail.contentDisplay, columns);
    setSavedColumns(next);
    if (sort && !next.some(column => column.id === sort.id && column.visible)) setSort(null);
    try {
      window.localStorage.setItem(storageKey(id), JSON.stringify(next));
    } catch {
      // Preferences still work when browser storage is unavailable.
    }
  };

  return <Table
    loading={loading}
    loadingText="Loading report"
    variant="embedded"
    stripedRows
    resizableColumns
    trackBy="id"
    ariaLabels={{ tableLabel: title }}
    items={sortedItems}
    columnDefinitions={columns}
    columnDisplay={columnDisplay}
    sortingColumn={sortingColumn}
    sortingDescending={sort?.isDescending || false}
    sortingDisabled={loading}
    onSortingChange={({ detail }) => setSort({ id: detail.sortingColumn.sortingField, isDescending: detail.isDescending })}
    header={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 32 }}>
      <div>{summary}</div>
      <CollectionPreferences
        title={`${title} preferences`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        preferences={{ contentDisplay: columnDisplay }}
        contentDisplayPreference={{
          title: 'Columns',
          options: columns.map((column, index) => ({ id: column.id, label: column.header, alwaysVisible: index === 0 })),
        }}
        onConfirm={confirmColumns}
      />
    </div>}
    empty={empty}
  />;
}
